import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SelfieCameraDialog } from '../../components/SelfieCameraDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Clock, 
  Camera,
  X,
  UserCheck,
  AlertTriangle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { differenceInMinutes } from 'date-fns';
import { formatUK, toUKDate } from '../../lib/ukTime';
import { isShiftEndPassedUK } from '../../lib/shiftTime';
import { cn } from '../../lib/utils';
import {
  captureLocationDetailed,
  locationErrorMessage,
  hasShownLocationNotice,
  markLocationNoticeShown,
} from '../../lib/geolocation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [todayShift, setTodayShift] = useState(null);
  const [nextShift, setNextShift] = useState(null);
  const [availableShiftsCount, setAvailableShiftsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  // Geofence block info ({code, distance_m, radius_m, site_name, message})
  const [geoBlock, setGeoBlock] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [allocatedBy, setAllocatedBy] = useState('');
  // For spontaneous (no-shift) clock-in, employee fills 3 fields then taps
  // Save which "locks" the answers and reveals the camera flow.
  const [allocatedPosition, setAllocatedPosition] = useState('');
  const [allocatedLocation, setAllocatedLocation] = useState('');
  const [spontaneousSaved, setSpontaneousSaved] = useState(false);
  const [lateReason, setLateReason] = useState('');
  const [shiftRole, setShiftRole] = useState('');
  // One-time pre-prompt for GPS capture. Shown before the very first
  // Clock In / Clock Out of this device so the employee understands
  // exactly why we ask the browser for permission.
  const [locNotice, setLocNotice] = useState({ open: false, kind: null }); // kind = 'in' | 'out'
  // Banner dismissal — when the user clicks X we hide it locally for the
  // session. Backend will stop returning the field after 1h anyway.
  const [autoClockOutDismissed, setAutoClockOutDismissed] = useState(false);

  useEffect(() => {
    // Always refresh the auth/user state when the dashboard mounts so a
    // stale React `user` object (e.g. tab kept open all day, or app opened
    // mid-day) cannot disagree with the backend about whether the employee
    // is currently clocked in.
    refreshUser().catch(() => {});
    fetchTodayShift();
    fetchAvailableShiftsCount();
    fetchNextShift();
  }, []);

  // Auto-derive the shift role from the scheduled shift so the employee
  // doesn't need to pick it manually. The role string (e.g. "SIA",
  // "Cleaner", "Dual role (SIA, cleaner)") is stored under `shift_role` on
  // the shifts collection (set via the admin Edit Shift dialog). Older
  // shifts may carry the same value under `position`, and a few really old
  // ones under `role`, so we check all three for backwards compatibility.
  useEffect(() => {
    if (todayShift && !shiftRole) {
      const role = (todayShift.shift_role || todayShift.position || todayShift.role || '').trim();
      if (role) setShiftRole(role);
    }
  }, [todayShift, shiftRole]);

  useAutoRefresh(() => {
    fetchTodayShift();
    fetchNextShift();
    fetchAvailableShiftsCount();
  });

  const fetchTodayShift = async () => {
    try {
      // Use the new /shifts/today endpoint which handles UK timezone on server
      const response = await axios.get(`${API}/shifts/today`);
      const { shifts, uk_date } = response.data;
      
      // Get all today's shifts sorted by start_time (backend sorts them)
      const todayShifts = shifts.filter(s => s.day_label === 'today');
      const yesterdayShift = shifts.find(s => s.day_label === 'yesterday' && s.status === 'in_progress') || shifts.find(s => s.day_label === 'yesterday');
      const tomorrowShift = shifts.find(s => s.day_label === 'tomorrow');
      
      // Only shifts EXPLICITLY labelled 'today' become the Today card.
      // If there is nothing today, we leave todayShift = null so the UI
      // doesn't mislabel a tomorrow/yesterday shift as "starting today"
      // (bug 2026-07: overnight shifts on the next day were showing up
      // in the Today card because we used to fall back to tomorrow).
      let currentShift = null;
      const openYesterday = yesterdayShift && yesterdayShift.status === 'in_progress' ? yesterdayShift : null;

      if (todayShifts.length > 0) {
        const inProgress = todayShifts.find(s => s.status === 'in_progress');
        const nextScheduled = todayShifts.find(s => s.status === 'scheduled');

        if (inProgress) {
          // Currently working this shift
          currentShift = inProgress;
        } else if (openYesterday) {
          // An overnight/covered shift from yesterday is STILL OPEN — it must
          // be clocked out first, so it wins over today's scheduled shift
          // (otherwise the employee only sees "Clock In" and has no way out).
          currentShift = openYesterday;
        } else if (nextScheduled) {
          // Next shift to work
          currentShift = nextScheduled;
        } else {
          // All completed — show the last one for reference
          currentShift = todayShifts[todayShifts.length - 1];
        }
      } else if (openYesterday) {
        // Overnight shift that started yesterday and is still running — keep
        // showing it so the employee can clock out from the Home page.
        currentShift = openYesterday;
      }

      setTodayShift(currentShift);
    } catch (error) {
      console.error('Failed to fetch shift:', error);
      // Fallback to old method if new endpoint fails
      try {
        const today = formatUK(new Date().toISOString(), 'yyyy-MM-dd');
        const response = await axios.get(`${API}/shifts`, { params: { date: today } });
        // Only accept shifts whose date EXACTLY matches today. Pick first
        // non-completed shift or the first one if all completed.
        const allShifts = (response.data || []).filter(s => s.date === today);
        const next = allShifts.find(s => s.status !== 'completed') || allShifts[0] || null;
        setTodayShift(next);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableShiftsCount = async () => {
    try {
      const response = await axios.get(`${API}/open-shifts`);
      const available = response.data.filter(s => s.status === 'available');
      setAvailableShiftsCount(available.length);
    } catch (error) {
      console.error('Failed to fetch available shifts:', error);
    }
  };

  // Pulls the employee's next scheduled shift AFTER today (UK day) so the
  // Home page can show a compact "what's next" reminder once today's shift
  // is completed. We only consider non-completed/non-missed rows so a
  // stale historical record can't pose as "next".
  const fetchNextShift = async () => {
    try {
      const res = await axios.get(`${API}/shifts/my`);
      // Compute today's date in Europe/London as YYYY-MM-DD so the string
      // comparison against shift.date stays correct around midnight.
      const todayUK = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date());
      const upcoming = (res.data || [])
        .filter(s => s.date && s.date > todayUK)
        .filter(s => s.status !== 'completed' && s.status !== 'missed')
        .sort((a, b) => {
          if (a.date !== b.date) return a.date < b.date ? -1 : 1;
          return (a.start_time || '').localeCompare(b.start_time || '');
        });
      setNextShift(upcoming[0] || null);
    } catch (error) {
      console.error('Failed to fetch next shift:', error);
      setNextShift(null);
    }
  };


  const validateAllocatedBy = (name) => {
    if (!name || !name.trim()) return false;
    
    const userNameLower = user?.name?.toLowerCase().trim() || '';
    const allocatedLower = name.toLowerCase().trim();
    
    // Check if any part of user's name is in allocated_by
    const userParts = userNameLower.split(' ').filter(p => p.length > 2);
    for (const part of userParts) {
      if (allocatedLower.includes(part)) {
        return false;
      }
    }
    
    // Check if allocated_by parts are in user's name
    const allocatedParts = allocatedLower.split(' ').filter(p => p.length > 2);
    for (const part of allocatedParts) {
      if (userNameLower.includes(part)) {
        return false;
      }
    }
    
    return true;
  };

  // Check if current time is more than 1 hour late for clock in
  const isLateClockIn = () => {
    if (!todayShift?.start_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.start_time.split(':').map(Number);
    const shiftStart = new Date(ukNow);
    shiftStart.setHours(hours, minutes, 0, 0);
    
    // For night shifts from yesterday, the start was yesterday
    if (todayShift.day_label === 'yesterday') {
      shiftStart.setDate(shiftStart.getDate() - 1);
    }
    
    const diffMins = differenceInMinutes(ukNow, shiftStart);
    return diffMins > 60; // More than 1 hour late
  };

  // Number of minutes from now until shift start (negative = already started).
  // Used by the strict 10-minute pre-clock-in gate.
  const minutesUntilShiftStart = () => {
    if (!todayShift?.start_time) return null;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.start_time.split(':').map(Number);
    const shiftStart = new Date(ukNow);
    shiftStart.setHours(hours, minutes, 0, 0);
    if (todayShift.day_label === 'tomorrow') {
      shiftStart.setDate(shiftStart.getDate() + 1);
    } else if (todayShift.day_label === 'yesterday') {
      shiftStart.setDate(shiftStart.getDate() - 1);
    }
    return differenceInMinutes(shiftStart, ukNow);
  };

  // True iff the employee is opening the app earlier than the 10-minute
  // pre-shift window. PENDING shifts (no start_time) are always allowed.
  const isTooEarlyToClockIn = () => {
    if (!todayShift?.start_time) return false;
    const m = minutesUntilShiftStart();
    return m !== null && m > 10;
  };

  // Check if current time is more than 1 hour past shift end for clock out
  const isLateClockOut = () => {
    if (!todayShift?.end_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.end_time.split(':').map(Number);
    const shiftEnd = new Date(ukNow);
    shiftEnd.setHours(hours, minutes, 0, 0);
    
    // For night shifts, end time is the next day from shift date
    const startTime = todayShift.start_time || '00:00';
    if (todayShift.end_time < startTime) {
      // Night shift — if shift is from yesterday, end is today (correct already)
      // If shift is from today, end is tomorrow (don't flag as late yet)
      if (todayShift.day_label === 'today') {
        return false; // End time is tomorrow, not late yet
      }
    }
    
    const diffMins = differenceInMinutes(ukNow, shiftEnd);
    return diffMins > 60; // More than 1 hour past end
  };

  const handleClockIn = async () => {
    if (!photo) {
      toast.error('Please take a site photo before clocking in');
      return;
    }
    
    if (!shiftRole) {
      toast.error('Please select your shift role before clocking in');
      return;
    }

    // Show the one-time GPS notice before the very first clock event so
    // the employee knows EXACTLY why the browser is about to prompt for
    // location permission. Subsequent clock events skip the dialog.
    if (!hasShownLocationNotice()) {
      setLocNotice({ open: true, kind: 'in' });
      return;
    }
    await performClockIn();
  };

  // Actual network work — split out so the location-notice dialog can
  // re-trigger it after the employee acknowledges the disclosure.
  const performClockIn = async () => {
    // Home page is STRICT MODE only — the render gate above guarantees this
    // button is reachable only when `todayShift` exists with confirmed
    // start_time/end_time. Spontaneous (no-shift) clock-ins live on the
    // dedicated Manual Clock In page, which collects `allocated_by` itself.
    // So we never ask for allocated_by here — even if the shift was created
    // with PENDING times that have since been filled, the schedule itself
    // already authorises the clock-in.

    setClockLoading(true);
    try {
      // MANDATORY GPS: clock-in is blocked without a real location fix.
      // The backend enforces the same rule, so this cannot be bypassed.
      const gps = await captureLocationDetailed();
      if (!gps.loc) {
        setClockLoading(false);
        toast.error(locationErrorMessage(gps.status), { duration: 9000 });
        return;
      }
      const loc = gps.loc;
      const payload = {
        shift_id: todayShift?.id,
        allocated_by: null,
        allocated_position: null,
        allocated_location: null,
        late_reason: null,
        shift_role: shiftRole,
        latitude: loc?.latitude ?? null,
        longitude: loc?.longitude ?? null,
        accuracy: loc?.accuracy ?? null,
      };
      
      const response = await axios.post(`${API}/clock/in`, payload);

      // Show success + reset UI IMMEDIATELY so the employee isn't stuck on
      // "Please wait..." while the photo (which can be a large base64) uploads.
      toast.success('Thank you and have a good shift');
      setPhoto(null);
      setAllocatedBy('');
      setAllocatedPosition('');
      setAllocatedLocation('');
      setSpontaneousSaved(false);
      setLateReason('');
      setShiftRole('');
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();

      // Fire-and-forget photo upload
      axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: photo
      }).catch(() => {
        toast.error('Photo upload failed — your clock-in was saved');
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && (detail.code === 'OUT_OF_RANGE' || detail.code === 'LOCATION_REQUIRED')) {
        setGeoBlock(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to clock in');
      }
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!photo) {
      toast.error('Please take a site photo before clocking out');
      return;
    }
    if (!hasShownLocationNotice()) {
      setLocNotice({ open: true, kind: 'out' });
      return;
    }
    await performClockOut();
  };

  const performClockOut = async () => {
    // Late reason check removed — clock-out is always allowed without a reason.

    setClockLoading(true);
    try {
      // MANDATORY GPS: clock-out is blocked without a real location fix.
      // The backend enforces the same rule, so this cannot be bypassed.
      const gps = await captureLocationDetailed();
      if (!gps.loc) {
        setClockLoading(false);
        toast.error(locationErrorMessage(gps.status), { duration: 9000 });
        return;
      }
      const loc = gps.loc;
      const response = await axios.post(`${API}/clock/out`, {
        shift_id: todayShift?.id,
        late_reason: null,
        latitude: loc?.latitude ?? null,
        longitude: loc?.longitude ?? null,
        accuracy: loc?.accuracy ?? null,
      });

      // Instant UI reset — see handleClockIn for rationale.
      toast.success('Thank you for your work');
      setPhoto(null);
      setLateReason('');
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();

      axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: photo
      }).catch(() => {
        toast.error('Photo upload failed — your clock-out was saved');
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
      setClockLoading(false);
    }
  };

  const isOnShift = user?.is_on_shift;

  // True when today's scheduled shift's end_time has already passed in UK
  // time. Combined with !isOnShift this means the employee is effectively
  // off-shift for the day — even if the shift's `status` is still stuck on
  // 'in_progress' or 'scheduled' due to a missed auto-update somewhere.
  const isShiftEndPassed = isShiftEndPassedUK(todayShift);

  // FORGOT CLOCK OUT — still clocked in but the scheduled end has passed.
  // Exception: admin deleted the clock-out (shift cover) → keep the NORMAL
  // clock-out flow so the real leaving time is recorded.
  const forgotClockOut = isOnShift && todayShift && todayShift.status === 'in_progress' && !todayShift.reclock_out_actual && isShiftEndPassed;

  const handleForgotClockOut = async () => {
    setClockLoading(true);
    try {
      // MANDATORY GPS — same rule as a normal clock-out.
      const gps = await captureLocationDetailed();
      if (!gps.loc) {
        setClockLoading(false);
        toast.error(locationErrorMessage(gps.status), { duration: 9000 });
        return;
      }
      await axios.post(`${API}/clock/confirm-forgot-out`, {
        latitude: gps.loc.latitude ?? null,
        longitude: gps.loc.longitude ?? null,
        accuracy: gps.loc.accuracy ?? null,
      });
      toast.success('Previous shift closed at the scheduled end time');
      setPhoto(null);
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm clock out');
      setClockLoading(false);
    }
  };

  // Check if shift time has ended (using UK time)
  const isShiftEnded = () => {
    if (!todayShift || !todayShift.end_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const currentTotalMinutes = ukNow.getHours() * 60 + ukNow.getMinutes();
    
    const [endHours, endMinutes] = todayShift.end_time.split(':').map(Number);
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return currentTotalMinutes > endTotalMinutes;
  };

  const shiftEnded = isShiftEnded();

  if (loading) {
    return (
      <EmployeeLayout>
        <div className="py-8 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <div className="py-6 space-y-6">
        {/* GEOFENCE BLOCK BANNER — clock-in refused because the employee is
            outside the allowed radius (or GPS missing). Highly visible. */}
        {geoBlock && (
          <div className="border-2 border-red-500 bg-red-950/60 rounded-sm p-5 space-y-3 animate-pulse" data-testid="geo-block-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <p className="font-barlow text-xl uppercase tracking-wider text-red-400">
                  {geoBlock.code === 'LOCATION_REQUIRED' ? 'Location required' : 'Clock-in blocked — too far from site'}
                </p>
                <p className="text-sm text-red-200 mt-1" data-testid="geo-block-message">
                  {geoBlock.message}
                </p>
                {geoBlock.code === 'OUT_OF_RANGE' && (
                    <p className="text-xs text-red-300/80 mt-2">
                    Your distance: <strong>{geoBlock.distance_label || `${geoBlock.distance_m}m`}</strong> · Allowed: <strong>{geoBlock.radius_m}m</strong> · Site: <strong>{geoBlock.site_name}</strong>
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={() => setGeoBlock(null)}
              variant="outline"
              className="w-full rounded-sm border-red-500 text-red-300 hover:bg-red-900/40 uppercase tracking-wider text-xs"
              data-testid="geo-block-dismiss-btn"
            >
              I moved closer — try again
            </Button>
          </div>
        )}
        {/* AUTO CLOCK-OUT BANNER — visible only when backend returns
            `last_auto_clock_out_at` (within the last hour). Dismissible. */}
        {user?.last_auto_clock_out_at && !autoClockOutDismissed && (
          <Card className="bg-amber-900/20 border-amber-500/50 rounded-sm">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-amber-300 uppercase tracking-wider">Clocked Out Automatically</p>
                <p className="text-sm text-amber-200/90">You have been clocked out automatically. Any questions please get in touch with admin.</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAutoClockOutDismissed(true)}
                className="h-7 w-7 text-amber-300 hover:text-amber-200"
                data-testid="dismiss-auto-clockout-banner"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* TOO EARLY gate removed — employees can clock in any time before
            the shift; backend rounds early clock-in to the official start. */}

        {/* Welcome */}
        <div className="text-center">
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Welcome back</p>
          <h1 className="text-2xl font-barlow tracking-widest mt-1">{user?.name}</h1>
          <p className="text-xs text-muted-foreground font-mono mt-2">
            {formatUK(new Date().toISOString(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* COUNTDOWN BANNER — small status line above the shift card */}
        {todayShift && todayShift.start_time && todayShift.end_time && todayShift.status !== 'completed' && !isOnShift && (() => {
          try {
            const [sh, sm] = todayShift.start_time.split(':').map(Number);
            const now = new Date();
            const start = new Date();
            start.setHours(sh, sm, 0, 0);
            const diffMin = Math.round((start - now) / 60000);
            if (diffMin > 0) {
              const h = Math.floor(diffMin / 60);
              const m = diffMin % 60;
              const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
              const colour = diffMin <= 60 ? 'text-amber-400 border-amber-400/40' : 'text-emerald-400 border-emerald-400/40';
              const dot = diffMin <= 60 ? '🟡' : '🟢';
              return (
                <div className={`text-center text-xs border rounded-sm py-1.5 ${colour}`} data-testid="shift-countdown">
                  {dot} Shift starts in {label}
                </div>
              );
            }
            if (diffMin > -60) {
              return (
                <div className="text-center text-xs border border-emerald-400/40 text-emerald-400 rounded-sm py-1.5" data-testid="shift-countdown-started">
                  🟢 Shift starts today at {todayShift.start_time}
                </div>
              );
            }
            return null;
          } catch { return null; }
        })()}

        {/* PENDING SCHEDULE — shift exists but admin hasn't confirmed hours yet */}
        {todayShift && (!todayShift.start_time || !todayShift.end_time) && todayShift.status !== 'completed' && (
          <Card className="bg-amber-900/15 border-amber-500/40 rounded-sm">
            <CardContent className="p-5 space-y-2 text-center">
              <p className="text-[10px] text-amber-400 uppercase tracking-[0.25em]">Today's shift</p>
              <p className="text-base font-barlow uppercase tracking-wider text-amber-300">⏳ Pending Schedule</p>
              <p className="text-xs text-amber-200/80">
                You have a shift assigned for today, but hours haven't been confirmed yet.
                Your supervisor will update them soon.
              </p>
              {(todayShift.site_name || todayShift.location) && (
                <p className="text-sm text-foreground/90 pt-1">📍 {todayShift.site_name || todayShift.location}</p>
              )}
              {(todayShift.shift_role || todayShift.position || todayShift.role) && (
                <p className="text-sm text-foreground/80">👔 {todayShift.shift_role || todayShift.position || todayShift.role}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* TODAY'S SHIFT — primary focus when employee has a real scheduled
            shift with confirmed hours. Big, easy to scan: time, duration,
            location, position. Hidden once the shift has ended (we then show
            the green "Shift Completed/Ended" card below). */}
        {todayShift && todayShift.start_time && todayShift.end_time && todayShift.status !== 'completed' && !(isShiftEndPassed && !isOnShift) && (
          <Card className="bg-primary/10 border-primary/40 rounded-sm">
            <CardContent className="p-5 space-y-2">
              <p className="text-[10px] text-primary uppercase tracking-[0.25em]">Today's shift</p>
              <p className="text-2xl font-barlow font-bold tracking-wider text-foreground" data-testid="today-shift-time">
                {todayShift.start_time} – {todayShift.end_time}
              </p>
              {(() => {
                try {
                  const [sh, sm] = todayShift.start_time.split(':').map(Number);
                  const [eh, em] = todayShift.end_time.split(':').map(Number);
                  let mins = (eh * 60 + em) - (sh * 60 + sm);
                  if (mins <= 0) mins += 24 * 60; // overnight
                  const h = Math.floor(mins / 60);
                  const m = mins % 60;
                  const label = m === 0
                    ? `${h} ${h === 1 ? 'hour' : 'hours'}`
                    : `${h}h ${m}m`;
                  return (
                    <p className="text-xs text-muted-foreground font-mono" data-testid="today-shift-duration">
                      {label}
                    </p>
                  );
                } catch { return null; }
              })()}
              <p className="text-sm text-foreground/90 pt-1" data-testid="today-shift-location">
                📍 {todayShift.site_name || todayShift.location || 'Location TBD'}
              </p>
              <p className="text-sm text-foreground/80" data-testid="today-shift-position">
                👔 {todayShift.shift_role || todayShift.position || todayShift.role || 'Position TBD'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* NO SCHEDULED SHIFT TODAY — strict mode: clock-in is impossible
            unless admin/supervisor has scheduled the employee. */}
        {!isOnShift && !todayShift && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-5 text-center space-y-2">
              <Clock className="w-10 h-10 text-muted-foreground mx-auto" strokeWidth={1.5} />
              <p className="text-base font-semibold uppercase tracking-wider text-foreground">No Scheduled Shift</p>
              <p className="text-sm text-muted-foreground">
                You have no confirmed shift for today.
              </p>
              <p className="text-xs text-muted-foreground">
                Contact your supervisor or admin if you believe this is an error.
              </p>
            </CardContent>
          </Card>
        )}

        {/* SHIFT COMPLETED — shown after clock-out for today's scheduled shift.
            Blocks the clock-in/out UI so the dashboard returns to a clean,
            "done for today" state instead of looping back to a clock-in form.
            Also handles the edge-case where the shift's end_time has already
            passed and the employee is no longer on shift (e.g. auto-clock-out
            ran, admin manually closed it, or a self-heal flag reset). */}
        {!isOnShift && todayShift && (todayShift.status === 'completed' || isShiftEndPassed) && (
          <Card className="bg-green-900/20 border-green-500/40 rounded-sm" data-testid="shift-completed-card">
            <CardContent className="p-5 text-center space-y-2">
              <UserCheck className="w-10 h-10 text-green-400 mx-auto" />
              <p className="text-base font-semibold text-green-300 uppercase tracking-wider">
                {todayShift.status === 'completed' ? 'Shift Completed' : 'Shift Ended'}
              </p>
              <p className="text-sm text-green-200/90">
                {todayShift.status === 'completed'
                  ? "You've clocked out for today's shift. Thank you for your work!"
                  : "Your shift has ended. If you forgot to clock out, you have been clocked out automatically. Any questions please contact admin."}
              </p>
              {todayShift.start_time && todayShift.end_time && (
                <p className="text-xs text-green-200/70 font-mono">
                  {todayShift.start_time} – {todayShift.end_time} @ {todayShift.site_name || 'site'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* NEXT SHIFT — compact reminder of what's coming up next. Shown
            after today's shift is completed OR when employee has nothing
            scheduled for today. Renders an empty-state when there's no
            upcoming shift so the employee knows the rota is clear. */}
        {!isOnShift && (todayShift?.status === 'completed' || isShiftEndPassed || !todayShift) && (() => {
          // Friendly relative day label: "Tomorrow" / weekday name / full date.
          const formatNextDay = (iso) => {
            if (!iso) return '';
            const todayUK = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'Europe/London',
              year: 'numeric', month: '2-digit', day: '2-digit',
            }).format(new Date());
            const t = new Date(todayUK + 'T00:00:00');
            const tomorrow = new Date(t);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowISO = tomorrow.toISOString().slice(0, 10);
            if (iso === tomorrowISO) return 'Tomorrow';
            const d = new Date(iso + 'T00:00:00');
            const diffDays = Math.round((d - t) / 86400000);
            if (diffDays > 1 && diffDays < 7) {
              return d.toLocaleDateString('en-GB', { weekday: 'long' });
            }
            return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
          };
          return (
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-4 text-center space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Next Shift</p>
                {nextShift ? (
                  <>
                    <p className="text-sm font-medium text-foreground">{formatNextDay(nextShift.date)}</p>
                    {nextShift.start_time && nextShift.end_time && (
                      <p className="text-base font-mono text-foreground">
                        {nextShift.start_time} – {nextShift.end_time}
                      </p>
                    )}
                    {nextShift.site_name && (
                      <p className="text-sm text-muted-foreground">{nextShift.site_name}</p>
                    )}
                    {nextShift.position && (
                      <p className="text-xs text-muted-foreground/80">{nextShift.position}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No upcoming shift</p>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* My Shifts Section */}
        {/* Shift Role card removed — role is auto-derived from the scheduled
            shift's `position` field, so the employee doesn't need to pick it.
            Saves them a tap and keeps the clock-in screen minimal. */}

        {/* No Scheduled Shift form removed — strict mode allows clock-in
            only when admin has scheduled the employee. Shown as a static
            info card above. */}

        {/* Late Reason cards removed — clock-in/out no longer requires a
            reason for late actions. */}

        {/* Enforced in-app selfie camera — no gallery access */}
        <SelfieCameraDialog
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(dataUrl) => setPhoto(dataUrl)}
        />

        {/* Single area: Camera OR Clock Button. Strict-mode gating:
              - employee is on shift (clock-out flow), OR
              - employee has a real, confirmed shift (any time of day).
            Too-early gate removed — backend rounds early clock-in to shift start.
            Spontaneous (no-shift) clock-ins are not permitted. */}
        {(() => {
          if (forgotClockOut) {
            return (
              <div className="py-4">
                <div className="border-2 border-primary bg-primary/10 rounded-md p-6 space-y-4" data-testid="forgot-clockout-panel">
                  <p className="text-center font-barlow text-2xl sm:text-3xl uppercase tracking-widest text-primary font-bold">
                    You forgot to clock out
                  </p>
                  <Button
                    onClick={handleForgotClockOut}
                    disabled={clockLoading}
                    data-testid="confirm-forgot-clockout-btn"
                    className="w-full h-24 rounded-md text-2xl font-barlow uppercase tracking-widest bg-primary text-primary-foreground hover:bg-yellow-300"
                  >
                    {clockLoading ? 'Please wait...' : 'Confirm Clock Out'}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Your clock-out time will be recorded as your scheduled shift end ({todayShift.end_time}).
                  </p>
                </div>
              </div>
            );
          }
          const alreadyDoneToday = !isOnShift && todayShift && (todayShift.status === 'completed' || isShiftEndPassed);
          const noShiftToday = !isOnShift && (!todayShift || !todayShift.start_time || !todayShift.end_time);
          if (alreadyDoneToday || noShiftToday) {
            return null;
          }
          return (
        <div className="py-4">
          {!photo ? (
            /* No photo yet — show camera + selfie hint */
            <>
              <p className="text-center text-xs text-muted-foreground tracking-wide mb-2" data-testid="selfie-message">
                Take a selfie to clock {isOnShift ? 'out' : 'in'}
              </p>
              <div
                className="w-full h-36 rounded-md border-2 border-dashed border-primary/50 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors bg-primary/5"
                onClick={() => setCameraOpen(true)}
                data-testid="take-photo-area"
              >
                <Camera className="w-16 h-16 text-primary mb-2" strokeWidth={1.75} />
                <p className="text-lg text-primary font-barlow uppercase tracking-widest font-bold">
                  📷 TAKE SELFIE
                </p>
              </div>
            </>
          ) : isOnShift ? (
            /* Photo taken + On duty → CLOCK OUT */
            <Button
              onClick={handleClockOut}
              disabled={clockLoading}
              data-testid="clock-out-btn"
              className="w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest transition-all bg-destructive hover:bg-red-600 text-foreground"
            >
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-10 h-10" strokeWidth={1.5} />
                {clockLoading ? 'Please wait...' : 'Clock Out'}
              </div>
            </Button>
          ) : (!shiftRole) ? (
            /* Photo taken but role somehow missing — show what's needed.
               Should be rare since shiftRole is auto-derived from shift.position. */
            <div className="space-y-3">
              <Button
                disabled
                className="w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest bg-primary text-primary-foreground opacity-40"
              >
                <div className="flex flex-col items-center gap-2">
                  <Clock className="w-10 h-10" strokeWidth={1.5} />
                  Clock In
                </div>
              </Button>
              <p className="text-xs text-center text-yellow-500">
                Shift role missing — contact your supervisor
              </p>
            </div>
          ) : (
            /* Photo taken + all requirements met → CLOCK IN */
            <Button
              onClick={handleClockIn}
              disabled={clockLoading}
              data-testid="clock-in-btn"
              className="w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest transition-all bg-primary hover:bg-yellow-300 text-primary-foreground tactical-glow"
            >
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-10 h-10" strokeWidth={1.5} />
                {clockLoading ? 'Please wait...' : 'Clock In'}
              </div>
            </Button>
          )}
        </div>
          );
        })()}

      </div>

      {/* One-time GPS disclosure notice — shown only before the very first
          clock event from this device. Acknowledging it sets a localStorage
          flag and immediately re-triggers the captured action (Clock In or
          Clock Out). Declining still allows the clock event to continue
          (the helper resolves to null on permission denial). */}
      <AlertDialog
        open={locNotice.open}
        onOpenChange={(o) => { if (!o) setLocNotice({ open: false, kind: null }); }}
      >
        <AlertDialogContent className="bg-card border-border rounded-sm" data-testid="location-notice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              Location notice
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Your location will only be recorded when you clock in or clock out.
              This is used for attendance verification and audit purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                markLocationNoticeShown();
                const kind = locNotice.kind;
                setLocNotice({ open: false, kind: null });
                // Re-trigger the action the employee actually pressed.
                if (kind === 'in') performClockIn();
                else if (kind === 'out') performClockOut();
              }}
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm w-full"
              data-testid="location-notice-ok"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EmployeeLayout>
  );
}
